import React, { useEffect, useRef, useState, useCallback } from 'react';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import { useActiveRightSidebarState } from '@jira/platform__controllers-layout/src/controllers/layout-controller/consumers/right-sidebar-controller/index.tsx';
import { FabricChannel } from '@atlaskit/analytics-listeners/types';
import { PanelSplitter } from '@atlaskit/navigation-system/layout/panel-splitter';
import { CONVERSATION_ASSISTANT_CONTAINER_ID } from '@atlassian/conversation-assistant/constants';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { useRovoExperienceAPIStore } from '@atlassian/rovo-experience-api/rovo-experience-api-store';
import { ConversationAssistantUIModes } from '@atlassian/conversation-assistant/types';
import { JiraConversationAssistant } from './JiraConversationAssistant.tsx';
import { useConversationAssistantSidebarApi } from './useConversationAssistantSidebarApi.tsx';

const panelSplitter = defineMessage({
	id: 'platform-conversation-assistant.panelSplitter.label',
	defaultMessage: 'Resize panel',
	description: 'label text for a button that will allow resizing of the right side panel',
});

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export {
	CONVERSATION_ASSISTANT_CONTAINER_ID,
	CONVERSATION_ASSISTANT_CONTAINER_WIDTH,
} from '@atlassian/conversation-assistant/constants';

const PanelInLayout = () => {
	const { formatMessage } = useIntl();
	const rightSidebarCurrentState = useActiveRightSidebarState();
	const [localPanelId, setLocalPanelId] = useState<unknown>();
	const prevPanelIdRef = useRef<unknown>();
	const delayRef = useRef<unknown>(null);
	const panelId = rightSidebarCurrentState?.panelId;
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { toggleConversationAssistantUIMode } = useRovoExperienceAPIStore();
	const sidebarApi = useConversationAssistantSidebarApi({});

	const toggleSidebar = useCallback(() => {
		toggleConversationAssistantUIMode({
			targetMode: ConversationAssistantUIModes.SIDEBAR,
			sidebarApi,
		});
	}, [toggleConversationAssistantUIMode, sidebarApi]);

	const handleResizeComplete = useCallback((): void => {
		createAnalyticsEvent({
			action: 'resized',
			actionSubject: 'aiMatePanel',
			tags: ['atlaskit', 'aiMate'],
		}).fire(FabricChannel.aiMate);
	}, [createAnalyticsEvent]);

	/**
	 * This use effect is used to add (when is necessary) a delay
	 * in the panelId => localPanelId syncing
	 */
	useEffect(() => {
		// @ts-expect-error - TS2769 - No overload matches this call.
		clearTimeout(delayRef.current);

		if (prevPanelIdRef.current === CONVERSATION_ASSISTANT_CONTAINER_ID) {
			/**
			 * The right-sidebar is showing the conversation assistant...
			 */
			if (
				rightSidebarCurrentState &&
				panelId !== undefined &&
				!rightSidebarCurrentState.isCollapsed &&
				!rightSidebarCurrentState.isMinimised
			) {
				/**
				 * and is open, not collapsed and we want to show a panel
				 * with an ID !== CONVERSATION_ASSISTANT_CONTAINER_ID
				 */
				setLocalPanelId(panelId);
			} else {
				/**
				 * and we want to close it
				 */
				delayRef.current = setTimeout(() => {
					setLocalPanelId(panelId);

					createAnalyticsEvent({
						action: 'closed',
						actionSubject: 'aiMatePanel',
						source: 'AIMate',
						tags: ['atlaskit', 'aiMate'],
					}).fire(FabricChannel.aiMate);
				}, 310);
			}
		} else {
			/**
			 * If the right sidebar is not showing the conversation assistant,
			 * we just keep localPanelId in sync with panelId
			 */
			setLocalPanelId(panelId);
		}

		prevPanelIdRef.current = panelId;

		// @ts-expect-error - TS2769 - No overload matches this call.
		return () => clearTimeout(delayRef.current);
	}, [createAnalyticsEvent, panelId, rightSidebarCurrentState]);

	if (localPanelId !== CONVERSATION_ASSISTANT_CONTAINER_ID) {
		return null;
	}

	return (
		<>
			<PanelSplitter label={formatMessage(panelSplitter)} onResizeEnd={handleResizeComplete} />
			<JiraConversationAssistant onClosePanel={toggleSidebar} />
		</>
	);
};

const SafeConversationAssistantPanel = (): React.JSX.Element => (
	<JSErrorBoundary
		fallback="unmount"
		id="right-side-conversation-assistant-panel"
		packageName="jiraConversationAssistant"
	>
		<PanelInLayout />
	</JSErrorBoundary>
);
export { SafeConversationAssistantPanel };
